import { RxCross2 } from 'react-icons/rx';

interface LogEntryModalProps {
    isOpen: boolean;
    onClose: () => void;
    logEntryData: Record<string, any> | null;
    title?: string;
}

const LogEntryModal: React.FC<LogEntryModalProps> = ({
    isOpen,
    onClose,
    logEntryData: data,
    title = 'Details',
}) => {
    const formatValue = (value: any): string => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    };
    
    const formatChangeValue = (oldValue: any, newValue: any): string => {
        const formatSingle = (val: any) => {
            if (val === null || val === undefined) return 'N/A';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            if (typeof val === 'object') return JSON.stringify(val);
            return String(val);
        };
        
        return `${formatSingle(oldValue)} → ${formatSingle(newValue)}`;
    };
    
    // Detect if this is a changes object (has arrays with [old, new] values)
    const isChangesData = data && Object.values(data).some(
        value => Array.isArray(value) && value.length === 2
    );
    
    // Detect if this is an MIP payload (has mip_payload property)
    const isMipPayload = data && 'mip_payload' in data;
    
    // Separate simple fields from nested objects
    const separateFields = (data: Record<string, any>) => {
        const simpleFields: Record<string, any> = {};
        const nestedFields: Record<string, any> = {};
        
        Object.entries(data).forEach(([key, value]) => {
            if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
                simpleFields[key] = value;
            } else {
                nestedFields[key] = value;
            }
        });
        
        return { simpleFields, nestedFields };
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-lg">
                <button
                    className="text-gray-600 hover:text-gray-900 absolute right-4 top-4"
                    onClick={onClose}
                >
                    <RxCross2 size={24} />
                </button>
                
                <div className="p-8">
                    <h2 className="mb-6 text-[22px] font-bold text-gray-900">
                        {isChangesData ? 'Changes' : isMipPayload ? 'MIP Payload' : 'Fund Request Snapshot'}
                    </h2>

                    <div className="mt-4 max-h-[60vh] overflow-y-auto">
                        {!data ? (
                            <div className="text-center text-gray-500">
                                No data available
                            </div>
                        ) : (() => {
                            const { simpleFields, nestedFields } = separateFields(data);
                            
                            return (
                                <div className="space-y-4">
                                    {/* Simple fields in a consolidated section */}
                                    {Object.keys(simpleFields).length > 0 && (
                                        <div className="rounded-lg border border-gray-300 bg-gray-50 p-4">
                                            <div className="mb-3 text-base font-bold text-gray-900">Fund Request Details</div>
                                            <div className="grid grid-cols-2 gap-3">
                                                {Object.entries(simpleFields).map(([key, value]) => {
                                                    const isChangeArray = Array.isArray(value) && value.length === 2;
                                                    
                                                    return (
                                                        <div key={key} className="text-sm">
                                                            <div className="font-semibold text-gray-700">
                                                                {key
                                                                    .split('_')
                                                                    .map(
                                                                        (word) =>
                                                                            word.charAt(0).toUpperCase() +
                                                                            word.slice(1)
                                                                    )
                                                                    .join(' ')}
                                                            </div>
                                                            <div className="text-gray-900">
                                                                {isChangeArray 
                                                                    ? formatChangeValue(value[0], value[1])
                                                                    : formatValue(value)
                                                                }
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Nested objects in separate cards */}
                                    {Object.entries(nestedFields).map(([key, value]) => (
                                        <div
                                            key={key}
                                            className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                                        >
                                            <div className="mb-1 text-sm font-semibold text-gray-700">
                                                {key
                                                    .split('_')
                                                    .map(
                                                        (word) =>
                                                            word.charAt(0).toUpperCase() +
                                                            word.slice(1)
                                                    )
                                                    .join(' ')}
                                            </div>
                                            <div className="whitespace-pre-wrap break-words text-sm text-gray-900">
                                                {formatValue(value)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            className="rounded-full border-[2px] border-gray-300 px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-gray-700 transition-colors duration-200 hover:border-gray-400 hover:bg-gray-50"
                            onClick={onClose}
                        >
                            CLOSE
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogEntryModal;
